import { logger } from '../utils/logger';
import { tokenStorage } from '../utils/tokenStorage';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  MERCHANT_APPLY: import.meta.env.VITE_API_V1_MERCHANTS_APPLY,
  MERCHANT_STATUS: import.meta.env.VITE_API_V1_MERCHANTS_STATUS,
  MERCHANT_RECORDS: import.meta.env.VITE_API_V1_MERCHANTS_RECORDS,
  MERCHANT_APPLICATION_CURRENT: import.meta.env.VITE_API_V1_MERCHANTS_APPLICATION_CURRENT,
  MERCHANT_APPLICATION_REAPPLY: import.meta.env.VITE_API_V1_MERCHANTS_APPLICATION_REAPPLY,
};

export interface MerchantApplicationData {
  name: string;
  nrcNumber: string;
  // NRC photos are optional based on current UI requirements
  nrcFrontImageFile?: File;
  nrcBackImageFile?: File;
  phoneNumber: string;
  address: string;
  email: string;
  businessName?: string;
  businessRegistrationNumber?: string;
  businessLicenseFile?: File;
  // Shop image is optional; only sent when isDisplayShop is true and file provided
  shopImageFile?: File;
  region: string;
  city: string;
  lat: string;
  lng: string;
  isDisplayShop: boolean;
  isPublic?: boolean;
}

export interface MerchantStatusResponse {
  success: boolean;
  status?: string | null;
  reason?: string | null;
  reviewedAt?: string | null;
  message?: string;
  error?: string;
}

export interface MerchantApplicationResponse {
  success: boolean;
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface MerchantRecord {
  id?: string;
  merchantId?: string;
  shopName?: string;
  name?: string;
  businessName?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  region?: string;
  city?: string;
  lat?: string;
  lng?: string;
  isDisplayShop?: boolean;
  isPublic?: boolean;
  status?: string;
  shopImageFile?: string;
  createdAt?: string;
  updatedAt?: string;
  reviewedAt?: string;
  deactivationReason?: string;
}

export interface MerchantRecordResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: MerchantRecord;
}

export interface CurrentApplicationResponse {
  success: boolean;
  message?: string;
  error?: string;
  statusCode?: number;
  data?: Partial<MerchantApplicationData> & {
    status?: string;
    reason?: string;
    reviewedAt?: string;
  };
}

// Helper function to get headers with authentication
const getHeaders = async () => {
  const accessToken = tokenStorage.getAccessToken();
  return {
    Accept: 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
};

export const merchantService = {
  // Apply for merchant registration
  applyForMerchant: async (
    applicationData: MerchantApplicationData,
  ): Promise<MerchantApplicationResponse> => {
    try {
      const formData = new FormData();

      // Add required fields
      formData.append('name', applicationData.name);
      formData.append('nrcNumber', applicationData.nrcNumber);
      formData.append('phoneNumber', applicationData.phoneNumber);
      formData.append('address', applicationData.address);
      formData.append('email', applicationData.email);

      // Add NRC photos if provided
      if (applicationData.nrcFrontImageFile) {
        formData.append('nrcFrontImageFile', applicationData.nrcFrontImageFile);
      }
      if (applicationData.nrcBackImageFile) {
        formData.append('nrcBackImageFile', applicationData.nrcBackImageFile);
      }

      // Add optional fields
      if (applicationData.businessName?.trim()) {
        formData.append('businessName', applicationData.businessName);
      }
      if (applicationData.businessRegistrationNumber?.trim()) {
        formData.append('businessRegistrationNumber', applicationData.businessRegistrationNumber);
      }
      if (applicationData.businessLicenseFile) {
        formData.append('businessLicenseFile', applicationData.businessLicenseFile);
      }

      // Add shop image file when provided
      if (applicationData.shopImageFile) {
        formData.append('shopImageFile', applicationData.shopImageFile);
      }

      // Add new required fields
      formData.append('region', applicationData.region);
      formData.append('city', applicationData.city);
      formData.append('lat', applicationData.lat);
      formData.append('lng', applicationData.lng);
      formData.append('isDisplayShop', applicationData.isDisplayShop.toString());
      // Append public visibility preference
      formData.append('isPublic', (applicationData.isPublic ?? false).toString());

      // Get headers with authentication
      const headers = await getHeaders();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;

      const response = await fetch(`${baseUrl}${API_ENDPOINTS.MERCHANT_APPLY}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        logger.log('Merchant application successful:', result);
        return {
          success: true,
          message: result.message || 'Application submitted successfully',
        };
      } else {
        logger.error('Merchant application failed:', result);
        return {
          success: false,
          error: result.message || 'Application failed',
        };
      }
    } catch (error) {
      logger.error('Merchant application error:', error);
      return {
        success: false,
        error: 'An error occurred while submitting the application',
      };
    }
  },

  // Check merchant status
  getMerchantStatus: async (): Promise<MerchantStatusResponse> => {
    try {
      const headers = await getHeaders();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;

      const response = await fetch(`${baseUrl}${API_ENDPOINTS.MERCHANT_STATUS}`, {
        method: 'GET',
        headers,
      });

      // Handle 404 specifically - no merchant application exists
      if (response.status === 404) {
        logger.log('No merchant application found (404)');
        return {
          success: true,
          status: null,
        };
      }

      const result = await response.json();

      if (response.ok) {
        logger.log('Merchant status check successful:', result);
        return {
          success: true,
          status: result.data?.status || result.status || 'unknown',
          reason: result.data?.reason ?? null,
          reviewedAt: result.data?.reviewedAt ?? null,
        };
      } else {
        // Other error responses
        logger.log('Merchant status check error:', result);
        return {
          success: true,
          status: null,
        };
      }
    } catch (error) {
      logger.error('Merchant status check error:', error);
      return {
        success: false,
        error: 'Failed to check merchant status',
      };
    }
  },

  // Get current merchant application (for prefilling on reapply)
  getCurrentApplication: async (): Promise<CurrentApplicationResponse> => {
    try {
      const headers = await getHeaders();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;

      const endpoint =
        API_ENDPOINTS.MERCHANT_APPLICATION_CURRENT || '/api/v1/merchants/applications/current';
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
      });

      if (response.status === 404) {
        logger.log('Current merchant application not found (404)');
        return { success: false, statusCode: 404, error: 'Not found' };
      }

      const result = await response.json();
      if (response.ok) {
        const data = result?.data ?? result;
        return { success: true, data };
      } else {
        logger.warn('Current application fetch failed:', result);
        return {
          success: false,
          error: result?.message || 'Failed to load current application',
          statusCode: response.status,
        };
      }
    } catch (error) {
      logger.error('Current application fetch error:', error);
      return { success: false, error: 'An error occurred while loading current application' };
    }
  },

  // Reapply for merchant (when status is rejected)
  reapplyForMerchant: async (
    applicationData: MerchantApplicationData,
  ): Promise<MerchantApplicationResponse> => {
    try {
      const formData = new FormData();

      // Required fields
      formData.append('name', applicationData.name);
      formData.append('nrcNumber', applicationData.nrcNumber);
      formData.append('phoneNumber', applicationData.phoneNumber);
      formData.append('address', applicationData.address);
      formData.append('email', applicationData.email);

      // Optional fields
      if (applicationData.businessName?.trim()) {
        formData.append('businessName', applicationData.businessName);
      }
      if (applicationData.businessRegistrationNumber?.trim()) {
        formData.append('businessRegistrationNumber', applicationData.businessRegistrationNumber);
      }

      // Files (optional)
      if (applicationData.nrcFrontImageFile) {
        formData.append('nrcFrontImageFile', applicationData.nrcFrontImageFile);
      }
      if (applicationData.nrcBackImageFile) {
        formData.append('nrcBackImageFile', applicationData.nrcBackImageFile);
      }
      if (applicationData.businessLicenseFile) {
        formData.append('businessLicenseFile', applicationData.businessLicenseFile);
      }
      if (applicationData.shopImageFile) {
        formData.append('shopImageFile', applicationData.shopImageFile);
      }

      // Location & visibility
      formData.append('region', applicationData.region);
      formData.append('city', applicationData.city);
      formData.append('lat', applicationData.lat);
      formData.append('lng', applicationData.lng);
      formData.append('isDisplayShop', applicationData.isDisplayShop.toString());
      formData.append('isPublic', (applicationData.isPublic ?? false).toString());

      // Headers
      const headers = await getHeaders();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;
      const endpoint =
        API_ENDPOINTS.MERCHANT_APPLICATION_REAPPLY || '/api/v1/merchants/applications/reapply';

      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        logger.log('Merchant reapply successful:', result);
        return { success: true, message: result?.message || 'Reapply submitted successfully' };
      } else {
        // Pass through 404 for fallback logic
        logger.warn('Merchant reapply failed:', result);
        return {
          success: false,
          error: result?.message || 'Reapply failed',
          statusCode: response.status,
        };
      }
    } catch (error) {
      logger.error('Merchant reapply error:', error);
      return { success: false, error: 'An error occurred while submitting reapply' };
    }
  },

  // Get merchant record by merchantId
  getMerchantRecord: async (merchantId: string): Promise<MerchantRecordResponse> => {
    try {
      if (!merchantId) {
        return { success: false, error: 'Merchant ID is required' };
      }

      const headers = await getHeaders();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;
      const endpoint = API_ENDPOINTS.MERCHANT_RECORDS?.replace('{merchantId}', merchantId);

      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
      });

      const result = await response.json();

      if (response.ok) {
        const data = result?.data ?? result;
        return {
          success: true,
          data: data as MerchantRecord,
          message: result?.message,
        };
      } else {
        logger.warn('Merchant record fetch failed:', result);
        return {
          success: false,
          error: result?.message || 'Failed to load merchant record',
        };
      }
    } catch (error) {
      logger.error('Merchant record fetch error:', error);
      return {
        success: false,
        error: 'An error occurred while loading merchant record',
      };
    }
  },
};

export default merchantService;
